import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { GitBranch, User, Calendar, ArrowRight } from 'lucide-react'
import api from '@/services/api'

interface DistributionItem {
  id: string
  bdrId: string
  bdrName: string
  leadId: string
  closerId: string | null
  dealId: string | null
  description: string
  date: string
}

export function BdrDistributionHistory({ bdrId }: { bdrId: string }) {
  const [items, setItems] = useState<DistributionItem[]>([])
  const [loading, setLoading] = useState(true)
  const [page, setPage] = useState(1)
  const [total, setTotal] = useState(0)

  useEffect(() => {
    if (!bdrId) return
    async function load() {
      setLoading(true)
      try {
        const { data } = await api.get('/bdr/distribution-history', {
          params: { bdrId, page, limit: 20 },
        })
        const d = data.data ?? data
        setItems(d.items ?? [])
        setTotal(d.total ?? 0)
      } catch {
        // silent
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [bdrId, page])

  function formatDate(iso: string) {
    const d = new Date(iso)
    return d.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: '2-digit', hour: '2-digit', minute: '2-digit' })
  }

  return (
    <div className="glass-card border-white/[0.06] p-5">
      <div className="flex items-center gap-2 text-text-primary font-extrabold text-xs uppercase tracking-widest mb-5">
        <GitBranch size={15} className="text-accent shrink-0" />
        Histórico de Distribuições
        {total > 0 && (
          <span className="text-[10px] font-bold text-text-muted bg-white/[0.04] px-2 py-0.5 rounded-lg border border-white/[0.06] ml-auto">
            {total} registros
          </span>
        )}
      </div>

      {loading ? (
        <div className="space-y-3 animate-pulse">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-10 bg-white/[0.04] rounded-lg" />
          ))}
        </div>
      ) : items.length === 0 ? (
        <div className="text-center py-8">
          <GitBranch size={24} className="mx-auto text-text-muted mb-2" />
          <p className="text-xs text-text-muted">Nenhuma distribuição registrada</p>
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((item, idx) => (
            <motion.div
              key={item.id}
              initial={{ opacity: 0, x: -8 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: idx * 0.03 }}
              className="flex items-center gap-3 p-3 bg-white/[0.02] border border-white/[0.04] rounded-xl hover:bg-white/[0.04] transition-all"
            >
              <div className="flex items-center gap-2 shrink-0">
                <User size={12} className="text-blue-400" />
                <span className="text-xs font-bold text-text-primary">{item.bdrName}</span>
              </div>

              <ArrowRight size={12} className="text-text-muted shrink-0" />

              <div className="flex items-center gap-1 shrink-0">
                <span className="text-[10px] font-bold text-text-muted">Deal</span>
                <span className="text-[10px] font-mono text-accent">{item.dealId?.slice(0, 8) ?? '—'}</span>
              </div>

              <div className="ml-auto flex items-center gap-1.5 shrink-0">
                <Calendar size={10} className="text-text-muted" />
                <span className="text-[10px] text-text-muted">{formatDate(item.date)}</span>
              </div>
            </motion.div>
          ))}

          {page * 20 < total && (
            <button
              onClick={() => setPage((p) => p + 1)}
              className="w-full py-2 text-xs font-bold text-accent hover:text-accent/80 transition-colors"
            >
              Carregar mais
            </button>
          )}
        </div>
      )}
    </div>
  )
}
